//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import HR from '@components/atoms/hr/HR';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import {
  BodyErrorText,
  BodyText,
  SectionText,
} from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { getItemStats } from '@uia/details/ItemStatsUtils';
import { StatsAndLocation } from '@uia/details/StatsAndLocation';
import { TaggedUsers } from '@uia/details/TaggedUsers';
import { UIADetailsReview } from '@uia/details/UIADetailsReview';
import { UserWebLink } from '@uia/details/UserWebLink';
import { ContentSourceBadge } from '@uia/details/badge/ContentSourceBadge';
import { ContentSourceType } from '@uia/details/badge/ContentSourceBadgeAvatar';
import { EditableCaption } from '@uia/details/captions/EditableCaption';
import { UIACaption } from '@uia/details/captions/UIACaption';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { useShouldRouteToExternal } from '@uia/details/hooks/useShouldRouteToExternal';
import { getIsUiaPreferredCaptionEditingEnabled } from '@uia/details/selectors/getUIAReactPortItemDetails';
import { getUserHref } from '@uia/details/utils/socialNetworkUtils';
import { isDefined } from '@utils/filterPredicates';
import { staticRelativeTime } from '@utils/staticRelativeTime';

export interface UIADetailsProps {
  cuid: Cuid;
}

export const UIADetails = ({ cuid }: UIADetailsProps) => {
  const { data: metadata, isError, isLoading } = useMediaMetadataQuery(cuid);

  const taggedUsers =
    metadata?.socialDetails.instagramDetails?.usersInPhoto.map((user) => ({
      id: user.userId,
      username: user.username,
    })) ?? [];

  const isCaptionEditingEnabled = useSelector(
    getIsUiaPreferredCaptionEditingEnabled,
  );

  const itemStats = getItemStats(metadata);
  const socialNetwork = metadata?.socialNetwork ?? SocialNetwork.Unknown;
  const followerCount = metadata?.socialDetails.followerCount;
  const originalCaption = metadata?.socialDetails.caption ?? '';
  const preferredCaption = metadata?.preferredCaption ?? originalCaption;
  const shouldLinkExternal = useShouldRouteToExternal(socialNetwork);

  const user = {
    fullName: metadata?.socialDetails.networkUserDisplayName,
    id: metadata?.socialDetails.networkUserId,
    profilePicture: metadata?.socialDetails.networkUserImageUrl,
    username: metadata?.networkUsername,
  };

  const socialNetworkId =
    shouldLinkExternal && socialNetwork === SocialNetwork.Facebook
      ? user.id
      : user.username;

  const userWebLink = (() => {
    if (isDefined(metadata?.originalUrl)) {
      return metadata.originalUrl;
    } else if (metadata?.socialNetwork === SocialNetwork.Instagram) {
      return getUserHref(shouldLinkExternal, socialNetwork, socialNetworkId);
    }
  })();

  if (socialNetwork === SocialNetwork.Bazaarvoice) {
    return (
      <UIADetailsReview
        isError={isError}
        isLoading={isLoading}
        metadata={metadata}
      />
    );
  }

  return (
    <DetailsWrapper>
      <SectionText>Media Details</SectionText>
      {isError && (
        <BodyErrorText>
          An unexpected error occurred. Please try again or contact Bazaarvoice
          Support.
        </BodyErrorText>
      )}
      {isLoading ? (
        <CenteredSpinner />
      ) : (
        <>
          {isDefined(metadata) && (
            <>
              {isDefined(user.username) && user.username !== '' && (
                <ContentSourceBadge
                  avatarUrl={user.profilePicture}
                  followerCount={followerCount}
                  hrefLink={getUserHref(
                    shouldLinkExternal,
                    socialNetwork,
                    socialNetworkId,
                  )}
                  isRouteToExternal={shouldLinkExternal}
                  mainTitle={user.username}
                  socialNetwork={socialNetwork}
                  sourceType={
                    isDefined(metadata.socialNetwork)
                      ? ContentSourceType.USER
                      : ContentSourceType.UPLOAD
                  }
                  subTitle={user.fullName}
                />
              )}
              <StatsAndLocation
                instagramLocationId={
                  metadata.socialDetails.instagramDetails?.locationId
                }
                instagramLocationName={
                  metadata.socialDetails.instagramDetails?.locationName
                }
                itemStats={itemStats}
              />
              {isCaptionEditingEnabled ? (
                <EditableCaption
                  cuid={metadata.rootCuid}
                  originalCaption={originalCaption}
                  preferredCaption={preferredCaption}
                  socialNetwork={socialNetwork}
                />
              ) : (
                <UIACaption
                  caption={preferredCaption}
                  socialNetwork={socialNetwork}
                />
              )}
              <TaggedUsers
                isRouteToExternal={shouldLinkExternal}
                socialNetwork={socialNetwork}
                taggedUsers={taggedUsers}
              />
              <HR />
              <NetworkDetails>
                {metadata.socialDetails.postedTime && (
                  <BodyText>
                    {staticRelativeTime(
                      metadata.socialDetails.postedTime,
                      true,
                    )}
                  </BodyText>
                )}
                <UserWebLink
                  href={userWebLink}
                  isProfile={!!metadata.originalUrl}
                  network={socialNetwork}
                />
              </NetworkDetails>
            </>
          )}
        </>
      )}
    </DetailsWrapper>
  );
};

const DetailsWrapper = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const NetworkDetails = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: ${theme.spacings[3]};
  `,
);
